import type {RelayReadinessEvidence} from '@github-ui/ahp-relay/relay-readiness'
import type {
  ActionEnvelope,
  AgentSelection,
  ChatInputAnswer,
  ChatInputRequest,
  ChatState,
  CompletionItem,
  MessageAttachment,
  ModelSelection,
  PendingMessage,
  ResourceReadResult,
  RootState,
  SessionState,
  ToolCallResult,
  URI,
} from '@microsoft/agent-host-protocol'
import type {SessionApprovalMode} from '@github-ui/ahp-relay/permission-mode'
import type {AcquireRelayLeaseOptions, RelayLeaseIdentity} from '@github-ui/ahp-relay/relay-registry'
import type {RelaySteeringMessage} from '@github-ui/ahp-relay/relay-steering-messages'
import type {
  ClientTool,
  RelayChatObserver,
  RelayChangesetObserver,
  RelayTerminalObserver,
  RelayModelCallFailure,
  RelaySessionRenameOptions,
  RelayStatus,
  RelayTelemetryObserver,
  RelayTurnDispatch,
} from '@github-ui/ahp-relay/relay-connection-runtime'
import type {ProvisioningStatus} from '@github-ui/ahp-relay/session-host/resolve-project'
import type {RelayFailure} from '@github-ui/ahp-relay/relay-failure'
import {createCopyObservations} from './copy-observation'
import type {UserFacingSubmissionError} from './submission-error'

import type {ConnectionAdmission} from '@github-ui/ahp-relay/connection-admission'

/** The message metadata namespace used for the browser-contributed per-turn instruction snapshot. */
export const CONTRIBUTED_INSTRUCTIONS_META_KEY = 'github.contributedInstructions' as const

export const SPACE_META_KEY = 'github.space'

export type ContributedInstructionKind = 'personal' | 'repository' | 'organization'

/**
 * A metadata-only description of one instruction set contributed by the browser.
 * Prompt text is intentionally not part of this contract.
 */
export interface ContributedInstructionMetadata {
  readonly kind: ContributedInstructionKind
  readonly label?: string
  readonly ownerUrl?: string
}

export type ContributedInstructionsMetadata = readonly ContributedInstructionMetadata[]

export interface EstablishedAhpSessionIdentity extends RelayLeaseIdentity {
  environmentId: string
  sessionId: string
}

/**
 * The three ids that name a connectable AHP session: the task it belongs to,
 * the environment it runs in, and the session itself.
 *
 * This lives beside the session contracts rather than in a UI package because
 * every layer that talks about "which session" — the composer, the shell, the
 * history list, the relay bootstrap — needs the same shape, and none of them
 * should have to reach up into a product package to name it.
 */
export interface AhpSessionIdentifiers {
  taskId: string
  environmentId: string
  sessionId: string
  channel?: string
}

/**
 * Identity used by passive history UI without implying that compute is
 * connectable. A row in a list knows its task but may not know, or need, a live
 * environment or session.
 */
export type AhpSessionDisplayIdentity = Pick<AhpSessionIdentifiers, 'taskId'> &
  Partial<Pick<AhpSessionIdentifiers, 'environmentId' | 'sessionId'>>

export interface PreparedPrompt {
  readonly text: string
  readonly attachments?: readonly MessageAttachment[]
  readonly model?: ModelSelection
  readonly agent?: AgentSelection
  readonly contributedInstructions?: ContributedInstructionsMetadata
  readonly displaySpace?: AhpSpaceSelection
  /**
   * Prompt the model receives in place of `text` on a dispatched turn. The
   * transcript and history keep showing `text`. Queued and steered messages
   * don't carry it.
   */
  readonly modelText?: string
}

export type PreparedPromptTransform = 'merge_contributed_instructions' | 'scope_contributed_instructions'
export const preparedPromptObservations = createCopyObservations<PreparedPrompt, PreparedPromptTransform>()

export interface CustomAgentSelectionRequest {
  readonly slug: string
  readonly displayName?: string
}

/** Creates the canonical prompt shape at a host boundary without carrying empty attachments. */
export function createPreparedPrompt(input: PreparedPrompt): PreparedPrompt {
  const attachments = input.attachments && input.attachments.length > 0 ? [...input.attachments] : undefined
  const contributedInstructions = input.contributedInstructions ? [...input.contributedInstructions] : undefined
  return preparedPromptObservations.copy(input, {
    text: input.text,
    ...(attachments ? {attachments} : {}),
    ...(input.model ? {model: input.model} : {}),
    ...(input.agent ? {agent: input.agent} : {}),
    ...(contributedInstructions ? {contributedInstructions} : {}),
    ...(input.modelText ? {modelText: input.modelText} : {}),
    ...(input.displaySpace ? {displaySpace: input.displaySpace} : {}),
  })
}

/**
 * Lower bound on what a prompt costs to store, in UTF-16 code units — the unit
 * browsers charge web storage against.
 *
 * Images and documents travel as base64 `data`, which dominates the total, so
 * the sum is taken from the strings already in memory rather than by copying or
 * serializing the prompt. Callers that need an exact figure measure the
 * serialized record instead; this is the cheap check that runs on the Send
 * interaction.
 */
export function preparedPromptPayloadChars(prompt: PreparedPrompt): number {
  let total = prompt.text.length
  for (const attachment of prompt.attachments ?? []) {
    total += attachment.label?.length ?? 0
    const data = (attachment as {data?: unknown}).data
    if (typeof data === 'string') total += data.length
  }
  return total
}

export interface EstablishedAhpPendingMessage {
  readonly id: string
  readonly prompt: PreparedPrompt
  readonly kind: 'queued' | 'steering'
}

function decodeContributedInstructions(raw: unknown): ContributedInstructionsMetadata | undefined {
  if (!Array.isArray(raw)) return undefined

  const instructions: ContributedInstructionMetadata[] = []
  for (const value of raw) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) continue

    const record = value as Record<string, unknown>
    const kind =
      record.kind === 'personal' || record.kind === 'repository' || record.kind === 'organization'
        ? record.kind
        : undefined
    if (!kind) continue

    instructions.push({
      kind,
      ...(typeof record.label === 'string' ? {label: record.label} : {}),
      ...(typeof record.ownerUrl === 'string' ? {ownerUrl: record.ownerUrl} : {}),
    })
  }

  return instructions.length > 0 ? instructions : undefined
}

export function createEstablishedAhpPendingMessage(message: PendingMessage): EstablishedAhpPendingMessage {
  const source = message.message
  const contributedInstructions = decodeContributedInstructions(source._meta?.[CONTRIBUTED_INSTRUCTIONS_META_KEY])

  const result: EstablishedAhpPendingMessage = {
    id: message.id,
    kind: 'queued',
    prompt: {
      text: source.text,
      ...(source.attachments ? {attachments: source.attachments} : {}),
      ...(source.model ? {model: source.model} : {}),
      ...(source.agent ? {agent: source.agent} : {}),
      ...(contributedInstructions ? {contributedInstructions} : {}),
    },
  }
  preparedPromptObservations.copy(source, result.prompt, undefined, false)
  return result
}

export interface DispatchReceipt {
  readonly requestId: string
  readonly clientSeq: number
  readonly turnId?: string
  readonly pendingMessageId?: string
}

export type DirectTurnKind = 'new_chat' | 'existing_chat'
export type DirectTurnStartPath = 'cold' | 'resume' | 'active'
const DIRECT_TURN_SURFACES = [
  'global_task',
  'repository_task',
  'agents_page',
  'repository_agents',
  'new_repository_task',
  'global_overlay',
  'dashboard',
  'embedded_chat',
  'explain_error',
] as const
export type DirectTurnSurface = (typeof DIRECT_TURN_SURFACES)[number]

export function isDirectTurnSurface(value: unknown): value is DirectTurnSurface {
  return typeof value === 'string' && DIRECT_TURN_SURFACES.some(surface => surface === value)
}

export interface DirectTurnTiming {
  readonly timeOriginMs: number
  readonly nowMs: number
}

export function isValidDirectTurnTiming(value: unknown): value is DirectTurnTiming {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false
  const timing = value as Record<string, unknown>
  return (
    typeof timing.timeOriginMs === 'number' &&
    Number.isFinite(timing.timeOriginMs) &&
    timing.timeOriginMs >= 0 &&
    typeof timing.nowMs === 'number' &&
    Number.isFinite(timing.nowMs) &&
    timing.nowMs >= 0 &&
    Number.isFinite(timing.timeOriginMs + timing.nowMs)
  )
}

export function captureDirectTurnTiming(): DirectTurnTiming {
  return {
    timeOriginMs: performance.timeOrigin,
    nowMs: performance.now(),
  }
}

export interface DirectTurnTelemetrySeed {
  readonly requestId: string
  readonly timing: DirectTurnTiming
  readonly surface: DirectTurnSurface
  readonly context?: DirectTurnTelemetryContext
}

export interface DirectTurnTelemetryContext {
  readonly surface: DirectTurnSurface
  readonly environmentId: string
  readonly taskId: string
  readonly sessionId: string
  readonly repositoryBacked: boolean
}

export interface DirectTurnSubmission {
  readonly requestId: string
  readonly turnKind: DirectTurnKind
  readonly startPath: DirectTurnStartPath
  readonly modelId?: string
  readonly surface?: DirectTurnSurface
  readonly context?: DirectTurnTelemetryContext
  readonly contextPending?: boolean
  readonly timing?: DirectTurnTiming
}

export type DirectTurnFinalizationReason = 'disposed' | 'page_exit' | 'replaced'

/**
 * Optional browser-local observer for one product's direct-turn telemetry.
 * Queue commands bypass this contract. Steering begins a provisional attempt
 * in case preparation outlives the active turn, then discards it if still steering.
 */
export interface EstablishedAhpDirectTurnTelemetry {
  readinessChanged(ready: boolean): void
  beginSubmission(submission: DirectTurnSubmission): void
  updateSubmissionAttribution(requestId: string, context: DirectTurnTelemetryContext, modelId?: string): void
  discardSubmission(requestId: string): void
  readinessRequired(requestId: string): void
  readinessObserved(requestId: string): void
  turnDispatching(requestId: string): void
  turnDispatched(requestId: string, turnId: string): void
  submissionContextResolved?(requestId: string, context: DirectTurnTelemetryContext | null): void
  preDispatchError(requestId: string): void
  acceptedAction(envelope: ActionEnvelope, previousState: ChatState | undefined, state: ChatState | undefined): void
  observedState(state: ChatState | undefined): void
  reset(reason?: DirectTurnFinalizationReason): void
}

export interface StopOutcome {
  readonly accepted: boolean
  readonly turnId?: string
}

export type SubmissionPhase = 'preparing' | 'unsent' | 'dispatched' | 'unknown' | 'accepted' | 'rejected'

export interface SubmissionAttempt {
  readonly requestId: string
  readonly prompt: PreparedPrompt
  readonly phase: SubmissionPhase
  readonly dispatch?: RelayTurnDispatch
  readonly receipt?: DispatchReceipt
}

export interface SubmissionRecovery {
  /** Stable for the page-local attempt store, including across recovery-controller replacement. */
  getSnapshot(): SubmissionAttempt | undefined
  subscribe(listener: () => void): () => void
  waitForAccepted(receipt: DispatchReceipt, timeoutMs?: number): Promise<void>
  preparationFailed?(prompt: PreparedPrompt, cause: unknown, requestId?: string): UserFacingSubmissionError
  recover(): Promise<SubmissionAttempt | undefined>
}

export interface EstablishedAhpSessionView {
  readonly generation: number
  /** True while this view's relay generation still owns its runtime, including after lease release. */
  isCurrent(): boolean
  getSnapshot(): EstablishedAhpSessionViewSnapshot
  subscribe(listener: () => void): () => void
  observeChat(channel: string, observer: RelayChatObserver): () => void
  observeChangeset?(channel: string, observer: RelayChangesetObserver): () => void
  observeTerminal?(channel: string, observer: RelayTerminalObserver): () => void
  readResource?(uri: string): Promise<ResourceReadResult>
  subscribeTelemetry?(observer: RelayTelemetryObserver): Promise<() => void>
}

export interface EstablishedAhpSessionViewSnapshot {
  readonly readiness?: RelayReadinessEvidence
  readonly state: ChatState | undefined
  readonly status: RelayStatus | 'idle'
  readonly reachable: boolean
  readonly error: string | undefined
  readonly failure?: RelayFailure
  readonly modelFailures: Record<string, RelayModelCallFailure> | undefined
  readonly provisioning: ProvisioningStatus | undefined
  readonly rootState?: RootState
  readonly sessionState: SessionState | undefined
  readonly passive: boolean
  readonly passiveKnown: boolean
  readonly steeringMessages: readonly RelaySteeringMessage[]
}

export interface EstablishedAhpSessionInputController {
  readonly recovery?: SubmissionRecovery
  submit(
    prompt: PreparedPrompt,
    requestId?: string,
    beforeDispatch?: () => Promise<void>,
    directTurnTelemetryContext?: DirectTurnTelemetryContext | null,
  ): Promise<DispatchReceipt>
  queue(prompt: PreparedPrompt, beforeDispatch?: () => Promise<void>): Promise<DispatchReceipt>
  /** Steers the live active turn, or submits a new turn if idle after preparation. */
  steer(
    prompt: PreparedPrompt,
    steeringId?: string,
    beforeDispatch?: () => Promise<void>,
    directTurnRequestId?: string,
  ): Promise<DispatchReceipt>
  sendNow(message: EstablishedAhpPendingMessage): Promise<DispatchReceipt>
  removeQueued(id: string): Promise<void>
}

export interface ToolConfirmationResponse {
  readonly approved: boolean
  readonly toolCallId: string
  readonly turnId: string
  readonly resultConfirmation?: boolean
  readonly selectedOptionId?: string
  readonly forSession?: boolean
  /** Chat channel that owns a session-level confirmation. */
  readonly chat?: URI
}

export type EstablishedAhpPromptCommandType =
  | 'ask_user_response'
  | 'elicitation_response'
  | 'permission_response'
  | 'plan_approval_response'

export interface EstablishedAhpPrompt {
  readonly toolCallId: string
  readonly toolName: string
  readonly arguments: unknown
}

export interface EstablishedAhpToolConfirmationOption {
  readonly id: string
  readonly label: string
  readonly kind: 'approve' | 'deny'
  readonly selectedOptionId?: string
  readonly forSession?: boolean
}

export interface EstablishedAhpToolConfirmation {
  readonly turnId: string
  readonly toolCallId: string
  readonly title: string
  /** Permission prompt kind from the host, when supplied. */
  readonly kind?: string
  readonly detail?: string
  readonly riskAssessment?: string
  readonly options: readonly EstablishedAhpToolConfirmationOption[]
  readonly resultConfirmation: boolean
  /** Originating chat for a session-level confirmation. */
  readonly chat?: URI
}

export interface InputCompletion {
  readonly requestId: string
  readonly response: 'accept' | 'decline' | 'cancel'
  readonly answers?: Record<string, ChatInputAnswer>
}

export interface EstablishedAhpSessionCommands {
  cancel(turnId?: string): StopOutcome
  claimActiveClient(tools: readonly ClientTool[]): Promise<void>
  completions(text: string, offset: number): Promise<CompletionItem[]>
  respondToPrompt(input: {
    requestId: string
    response: 'accept' | 'decline' | 'cancel'
    answers?: InputCompletion['answers']
  }): void
  confirmTool(input: ToolConfirmationResponse): void
  completeTool(input: {turnId: string; toolCallId: string; result: ToolCallResult}): void
  renameSession(title: string): void
  renameSession(title: string, options?: RelaySessionRenameOptions): void | Promise<void>
  setApprovalMode(mode: SessionApprovalMode): Promise<void>
  setApproveAll(enabled: boolean): Promise<void>
  setMode(mode: string): Promise<void>
  /** Probes the relay lane and starts resuming when the host is no longer reachable. */
  ensureLive?(admission?: ConnectionAdmission): Promise<void>
}

export interface EstablishedAhpPlanningState {
  readonly status: 'idle' | 'pending' | 'approved' | 'rejected' | 'failed'
  readonly plan?: EstablishedAhpPlan
  readonly error?: string
}

export interface EstablishedAhpPlan {
  readonly id: string
  readonly title?: string
  readonly summary?: string
  readonly steps: readonly string[]
}

export interface EstablishedAhpPlanningAdapter {
  identify(request: ChatInputRequest): EstablishedAhpPlan | undefined
  approve(plan: EstablishedAhpPlan): Promise<void>
  reject(plan: EstablishedAhpPlan): Promise<void>
  revise(plan: EstablishedAhpPlan, prompt: PreparedPrompt): Promise<void>
  continue(plan: EstablishedAhpPlan): Promise<void>
}

export interface EstablishedAhpPlanningPolicy {
  readonly enabled: boolean
  readonly adapter?: EstablishedAhpPlanningAdapter
}

export interface EstablishedAhpSessionPlanning {
  readonly state: EstablishedAhpPlanningState
  readonly actions: {
    approve(): Promise<void>
    reject(): Promise<void>
    revise(prompt: PreparedPrompt): Promise<void>
    continue(): Promise<void>
  }
}

export type EstablishedAhpLifecycleState = 'connecting' | 'waking' | 'live' | 'reconnecting' | 'failed' | 'disposed'

export interface EstablishedAhpSessionLifecycle {
  getState(): EstablishedAhpLifecycleState
  subscribe(listener: () => void): () => void
}

export interface EstablishedAhpSession {
  readonly identity: EstablishedAhpSessionIdentity
  readonly view: EstablishedAhpSessionView
  readonly commands: EstablishedAhpSessionCommands
  readonly input: EstablishedAhpSessionInputController
  readonly directTurnTelemetry?: EstablishedAhpDirectTurnTelemetry
  readonly planning?: EstablishedAhpSessionPlanning
  readonly lifecycle: EstablishedAhpSessionLifecycle
}

export interface EstablishedAhpSessionIntegrationServices {
  readonly references?: {
    resolve?: (reference: unknown) => Promise<readonly MessageAttachment[]> | readonly MessageAttachment[]
  }
  readonly attachments?: {
    prepare?: (attachment: unknown) => Promise<readonly MessageAttachment[]> | readonly MessageAttachment[]
  }
  readonly repository?: {
    getCurrent?: () => unknown
    select?: (repository: unknown) => void
  }
  readonly spaces?: {
    select?: (space: unknown) => void
  }
}

export interface EstablishedAhpSessionIntegrations extends EstablishedAhpSessionIntegrationServices {
  readonly view: EstablishedAhpSessionView
  readonly input: EstablishedAhpSessionInputController
  readonly commands: EstablishedAhpSessionCommands
  readonly lifecycle: EstablishedAhpSessionLifecycle
  readonly planning?: EstablishedAhpSessionPlanning
}

export interface FirstTurnHandoff {
  readonly requestId: string
  readonly prompt: PreparedPrompt
  readonly origin: 'new' | 'recovery'
  /**
   * Chat runtime mode selected for a new session. Bootstraps that create the
   * session send it as `createSession.config.mode`; `configureSession` should
   * only write it again when the host reports a different mode.
   */
  readonly mode?: string
  /**
   * Tool-approval mode selected for a new session. Bootstraps that create the
   * session send it as `createSession.config.approvalMode`; `configureSession`
   * should only write it again when the host reports a different mode.
   */
  readonly approvalMode?: SessionApprovalMode
  /** Applies session settings before the first user turn is dispatched. */
  readonly configureSession?: (session: EstablishedAhpSession, prompt?: PreparedPrompt) => Promise<void>
  /** Persists caller-owned state immediately before the relay sends the first turn. */
  readonly beforeDispatch?: () => Promise<void>
  /**
   * Hands the caller the first turn's dispatch receipt, so state armed in
   * `beforeDispatch` can record which turn it started. Observers run like the
   * direct-turn telemetry ones, synchronously and unguarded.
   */
  readonly afterDispatch?: (receipt: DispatchReceipt) => void
  /** Resolves session-advertised prompt metadata before the first user turn is dispatched. */
  readonly preparePrompt?: (session: EstablishedAhpSession, prompt: PreparedPrompt) => Promise<PreparedPrompt>
  readonly directTurnTelemetryContext?: DirectTurnTelemetryContext | null
  readonly directTurnTelemetryContextPending?: boolean
  /** Source retained while authoritative submission context is resolved after provisioning. */
  readonly directTurnTelemetrySurface?: DirectTurnSurface
  readonly directTurnTelemetryTiming?: DirectTurnTiming
  readonly onTurnDispatched?: (turnId: string) => void
}

export interface AhpSpaceSelection {
  readonly owner: string
  readonly id: number
  readonly slug: string
  readonly name: string
}

/**
 * Submission target metadata. `scope` may accompany `repository` as an owner fallback when
 * repository binding is disabled.
 */
export interface AhpSubmissionMetadata {
  readonly [key: string]: unknown
  readonly space?: {
    readonly owner: string
    readonly slug: string
  }
  readonly repository?: string
  /** Explicit composer selection, interpreted as binding intent only by environment-task provisioning. */
  readonly selectedRepository?: string
  /** Owner login scoping a sandbox provisioning request when no repository is attached. */
  readonly scope?: string
}

/**
 * Where a chat turn's sandbox should be provisioned. Repository and owner
 * scope targets remain independent of an attached Space. The Space target is
 * retained for compatibility with existing handoffs.
 */
export type AhpProvisioningTarget =
  | {readonly kind: 'space'; readonly space: AhpSpaceSelection}
  | {readonly kind: 'repository'; readonly nameWithOwner: string; readonly scopeOwner?: string}
  | {readonly kind: 'scope'; readonly owner: string}

export interface AhpChatStartRequest {
  readonly prompt: PreparedPrompt
  /** Repository custom agent to resolve against the established host catalogue. */
  readonly customAgent?: CustomAgentSelectionRequest
  /** The single resolved destination carried across hosts and navigation handoffs. */
  readonly provisioningTarget?: AhpProvisioningTarget
  readonly metadata?: AhpSubmissionMetadata
  /** Full picker state retained by the host while provisioning locks the Space. */
  readonly initialSelectedSpace?: AhpSpaceSelection
}

/**
 * Builds a start request from a resolved provisioning target.
 *
 * A selected Space is independent of the repository or owner scope that
 * provisions the sandbox. It travels as `{owner, slug}` for provisioning and,
 * when available, as the full picker selection shown by the destination.
 * A Space provisioning target remains supported for existing handoffs.
 */
export function createAhpChatStartRequest({
  prompt,
  target,
  customAgent,
  space,
  initialSelectedSpace,
}: {
  prompt: PreparedPrompt
  target?: AhpProvisioningTarget
  customAgent?: CustomAgentSelectionRequest
  space?: AhpSubmissionMetadata['space']
  initialSelectedSpace?: AhpSpaceSelection
}): AhpChatStartRequest {
  const selectedProvisioningSpace = target?.kind === 'space' ? target.space : space
  const provisioningSpace = selectedProvisioningSpace
    ? {owner: selectedProvisioningSpace.owner, slug: selectedProvisioningSpace.slug}
    : undefined
  const selectedSpace = target?.kind === 'space' ? target.space : initialSelectedSpace
  if (target?.kind === 'space') {
    return {
      prompt,
      ...(customAgent ? {customAgent} : {}),
      metadata: {space: provisioningSpace},
      initialSelectedSpace: selectedSpace,
    }
  }
  if (target?.kind === 'repository') {
    return {
      prompt,
      ...(customAgent ? {customAgent} : {}),
      metadata: {
        repository: target.nameWithOwner,
        ...(target.scopeOwner ? {scope: target.scopeOwner} : {}),
        ...(provisioningSpace ? {space: provisioningSpace} : {}),
      },
      ...(selectedSpace ? {initialSelectedSpace: selectedSpace} : {}),
    }
  }
  if (target?.kind === 'scope') {
    return {
      prompt,
      ...(customAgent ? {customAgent} : {}),
      metadata: {scope: target.owner, ...(provisioningSpace ? {space: provisioningSpace} : {})},
      ...(selectedSpace ? {initialSelectedSpace: selectedSpace} : {}),
    }
  }
  return {
    prompt,
    ...(customAgent ? {customAgent} : {}),
    ...(provisioningSpace ? {metadata: {space: provisioningSpace}} : {}),
    ...(selectedSpace ? {initialSelectedSpace: selectedSpace} : {}),
  }
}

export function createAhpChatStartRequestWithProvisioningTarget({
  prompt,
  target,
  space,
  initialSelectedSpace,
}: {
  prompt: PreparedPrompt
  target?: AhpProvisioningTarget
  space?: AhpSubmissionMetadata['space']
  initialSelectedSpace?: AhpSpaceSelection
}): AhpChatStartRequest {
  const request = createAhpChatStartRequest({prompt, target, space, initialSelectedSpace})
  return target ? {...request, provisioningTarget: target} : request
}
export interface ProvisionRequest {
  readonly firstTurn?: FirstTurnHandoff
  readonly metadata?: AhpSubmissionMetadata
}

export interface EstablishedAhpResumptionAttempt {
  readonly signal: AbortSignal
  readonly phase: EstablishedAhpResumptionPhase
  readonly timeoutMs: number
  /**
   * Wall-clock epoch ms mapped from a monotonic budget. Pausable attempts project now plus remaining
   * time, moving while paused. Abort, cancel, or dispose freezes the cutoff, including future values while paused.
   */
  readonly expiresAt: number
  readonly isDisposed: boolean
  remainingMs(): number
  advanceTo(phase: Exclude<EstablishedAhpResumptionPhase, 'initial'>): void
  waitFor<T>(work: Promise<T>): Promise<T>
  cancel(reason?: Error): void
  dispose(): void
}

export type EstablishedAhpResumptionPhase = 'initial' | 'waking' | 'connecting' | 'provisioning' | 'starting-agent'

export type EstablishRequest =
  | {
      readonly mode: 'provision'
      readonly input: ProvisionRequest
      /** Fires after provisioning resolves identity, before relay startup or first-turn acceptance. */
      readonly onIdentityResolved?: (identity: EstablishedAhpSessionIdentity) => void
    }
  | {
      /**
       * Retries relay/session establishment from the result of the latest
       * provision without invoking the product provisioning adapter again.
       */
      readonly mode: 'resume-provision'
      readonly firstTurn?: FirstTurnHandoff
    }
  | {
      readonly mode: 'attach'
      readonly connectionAdmission?: ConnectionAdmission
      readonly identity: EstablishedAhpSessionIdentity
      readonly resumptionAttempt?: EstablishedAhpResumptionAttempt
      readonly awaitIntentAfterLive?: boolean
    }
  | {
      readonly mode: 'wake'
      readonly connectionAdmission?: ConnectionAdmission
      readonly identity: EstablishedAhpSessionIdentity
      readonly resumptionAttempt?: EstablishedAhpResumptionAttempt
      readonly awaitIntentAfterLive?: boolean
    }
  | {
      readonly mode: 'recover'
      readonly connectionAdmission?: ConnectionAdmission
      readonly identity: EstablishedAhpSessionIdentity
      readonly firstTurn?: FirstTurnHandoff
      readonly resumptionAttempt?: EstablishedAhpResumptionAttempt
      readonly awaitIntentAfterLive?: boolean
    }

export interface EstablishedAhpSessionProvisioned {
  readonly identity: EstablishedAhpSessionIdentity
  readonly relay: AcquireRelayLeaseOptions
  readonly directTurnTelemetry?: EstablishedAhpDirectTurnTelemetry
}

export interface EstablishedAhpSessionBootstrapAdapter {
  provision(input: ProvisionRequest): Promise<EstablishedAhpSessionProvisioned>
  attach(identity: EstablishedAhpSessionIdentity): Promise<EstablishedAhpSessionProvisioned>
  wake(identity: EstablishedAhpSessionIdentity): Promise<EstablishedAhpSessionProvisioned>
  recover(identity: EstablishedAhpSessionIdentity): Promise<EstablishedAhpSessionProvisioned>
  reset?(): void
  dispose?(): void
}

export const ESTABLISHED_AHP_RESUMPTION_TIMEOUT_MS = 120_000

export type EstablishedAhpBootstrapState =
  | 'unbound'
  | 'provisioning'
  | 'history'
  | 'connecting'
  | 'waking'
  | 'reconnecting'
  | 'failed'
  | 'established'
  | 'disposed'

export interface EstablishedAhpSessionBootstrap {
  readonly state: EstablishedAhpBootstrapState
  readonly session: EstablishedAhpSession | undefined
  readonly directTurnTelemetry?: EstablishedAhpDirectTurnTelemetry
  /** Identity retained after provisioning so a failed establishment can resume without provisioning again. */
  readonly provisionedIdentity: EstablishedAhpSessionIdentity | undefined
  /**
   * Installs host-owned relay diagnostics before a new connection is created.
   * Existing relay entries retain the callbacks from their original acquire.
   */
  configureRelayDiagnostics?(diagnostics: EstablishedAhpSessionRelayDiagnostics): void
  establish(request: EstablishRequest): Promise<EstablishedAhpSession>
  /**
   * Accepts cancellation for the active first-turn handoff before the session is
   * published. The exact dispatched turn id is returned once it is available.
   */
  cancelPendingFirstTurn(): StopOutcome
  reset(): void
  dispose(): void
}

export type EstablishedAhpSessionRelayDiagnostics = Pick<
  AcquireRelayLeaseOptions,
  'webSocketFactory' | 'onStatus' | 'onDebugEvent'
>

export class EstablishedAhpSessionError extends Error {
  readonly operation: string
  readonly phase: EstablishedAhpBootstrapState | EstablishedAhpLifecycleState
  readonly recoverable: boolean
  readonly failure: RelayFailure | undefined

  constructor(
    operation: string,
    phase: EstablishedAhpBootstrapState | EstablishedAhpLifecycleState,
    message: string,
    recoverable: boolean,
    options?: ErrorOptions & {failure?: RelayFailure},
  ) {
    super(message, options)
    this.name = 'EstablishedAhpSessionError'
    this.operation = operation
    this.phase = phase
    this.recoverable = recoverable
    this.failure = options?.failure
  }
}

export class EstablishedAhpSessionResumptionTimeoutError extends EstablishedAhpSessionError {
  constructor(
    timeoutMs = ESTABLISHED_AHP_RESUMPTION_TIMEOUT_MS,
    phase?: EstablishedAhpResumptionPhase,
    newRepositoryLaunch = false,
  ) {
    const message =
      phase === 'provisioning'
        ? `The repository clone did not finish within ${timeoutMs}ms.`
        : phase === 'starting-agent'
          ? `The AHP agent did not become ready within ${timeoutMs}ms after repository preparation.`
          : phase === 'connecting'
            ? `The AHP connection did not become ready within ${timeoutMs}ms.`
            : newRepositoryLaunch && phase === 'initial'
              ? `The AHP environment did not respond within ${timeoutMs}ms.`
              : newRepositoryLaunch && phase === 'waking'
                ? `The AHP environment did not wake within ${timeoutMs}ms.`
                : `The existing AHP session did not become ready within ${timeoutMs}ms.`
    super('resume', 'reconnecting', message, true, {
      failure: phase === 'provisioning' ? {kind: 'provisioning', reason: 'timeout'} : {kind: 'resumption-timeout'},
    })
    this.name = 'EstablishedAhpSessionResumptionTimeoutError'
  }
}
